/*
 * Floretta: show only the photos of the selected variant. A variant with a photo list in its
 * Gallery metafield (custom.gallery) gets a carousel made from that list, in that order (see
 * "Variant galleries" below); any other variant is matched by alt text, as described next.
 * See snippets/floretta-variant-media.liquid for the alt text rules.
 *
 * Wraps District's MediaSlideshow#updateVariantImages instead of editing it. District hides a
 * slide when data-variant-image="true" and data-current-variant-image="false", so before
 * District's update runs, matching photos are marked as non-variant images (always shown) and
 * every other photo as a variant image; District then hides those and keeps the rest.
 *
 * Also keeps hidden photos from downloading. On start, District keeps detached copies of every
 * slide (MediaSlideshow#storeOriginalSlides), and browsers ignore loading="lazy" on detached
 * images, so a product with hundreds of photos downloaded all of them at full size. Before
 * District starts, hidden slides get their src/srcset parked in data attributes; they are put
 * back whenever District adds a slide to the page.
 */
(() => {
  const HIDDEN_SLIDES =
    'media-slideshow[data-hide-variants="true"] .splide__slide[data-variant-image="true"][data-current-variant-image="false"]';

  // A gallery carousel has every photo on the page, which could be dozens. All but the first main
  // photo are parked the same way, so only the ones swiped towards get downloaded. On page load
  // this is done by an inline script in snippets/media-slideshow.liquid, which runs earlier.
  const GALLERY_LATER_PHOTOS = [
    ".splide.media__main .splide__slide:not(:first-child) img",
    ".splide.media__thumbnails .splide__slide img",
    ".media__thumbnails--scrolling img",
  ].join(",");

  function park(img) {
    for (const attr of ["src", "srcset"]) {
      if (img.hasAttribute(attr)) {
        img.setAttribute(`data-floretta-${attr}`, img.getAttribute(attr));
        img.removeAttribute(attr);
      }
    }
  }

  function unpark(root) {
    root.querySelectorAll("img[data-floretta-src], img[data-floretta-srcset]").forEach((img) => {
      for (const attr of ["srcset", "src"]) {
        const value = img.getAttribute(`data-floretta-${attr}`);
        if (value !== null) {
          img.setAttribute(attr, value);
          img.removeAttribute(`data-floretta-${attr}`);
        }
      }
    });
  }

  // Variant galleries
  // A variant with a photo list in its Gallery metafield gets a carousel made from just that list,
  // in that order (snippets/floretta-gallery-slides.liquid), marked data-floretta-gallery="true".
  // Those photos aren't all on the page, so District's show/hide can't switch between them.
  // VariantSelects already fetches the new variant's product section on every variant change, so
  // take the carousel's slides from that response and swap them in (the hook is at the bottom).
  const slideIds = (slideshow) =>
    [...slideshow.querySelectorAll(".splide.media__main .splide__slide")].map((slide) => slide.dataset.mediaId).join();

  function adoptSlides(slideshow, next) {
    const { main, thumbnails } = slideshow;
    const isGallery = next.dataset.florettaGallery === "true";

    // A regular carousel has every photo, most of them hidden; park those as on page load.
    next.ownerDocument.querySelectorAll(`${HIDDEN_SLIDES} img`).forEach(park);
    if (isGallery) next.querySelectorAll(GALLERY_LATER_PHOTOS).forEach(park);
    const take = (selector) => [...next.querySelectorAll(selector)].map((element) => document.importNode(element, true));

    // Add the new slides before removing the old ones, so the carousel is never empty.
    const replace = (splide, root, slides) => {
      const old = new Set(root.querySelectorAll(".splide__slide"));
      splide.add(slides);
      splide.remove((slide) => old.has(slide.slide));
    };
    replace(main, slideshow.slideshow, take(".splide.media__main .splide__slide"));
    if (thumbnails && slideshow.slideshowThumbnails) {
      replace(thumbnails, slideshow.slideshowThumbnails, take(".splide.media__thumbnails .splide__slide"));
    }
    const scrolling = slideshow.mediaThumbnailsScrolling;
    if (scrolling) {
      const thumbs = take(".media__thumbnails--scrolling .media__thumbnail");
      scrolling.querySelector("ul").replaceChildren(...thumbs);
      slideshow.mediaThumbnails = scrolling.querySelectorAll(".media__thumbnail");
      thumbs.forEach((thumb) => thumb.addEventListener("click", (event) => slideshow.selectThumbnail(event)));
    }

    // District keeps copies of the slides it started with and which it has removed; start over.
    slideshow.originalMainSlides = [];
    slideshow.originalThumbnailSlides = [];
    slideshow.originalThumbnailElements = [];
    slideshow.removedSlides.clear();
    slideshow.storeOriginalSlides();
    unpark(slideshow); // District's copies stay parked, so they don't download

    if (isGallery) {
      slideshow.dataset.florettaGallery = "true";
    } else {
      delete slideshow.dataset.florettaGallery;
    }
    slideshow.florettaStartAtFirst = isGallery;
    const slides = [...slideshow.slideshow.querySelectorAll(".splide__slide")];
    slides.forEach((slide, index) => slide.setAttribute("data-slide-index", index));
    if (!isGallery && slideshow.dataset.hideVariants === "true") slideshow.removeSlides();

    slideshow.updateLightboxImages();
    main.refresh();
    thumbnails?.refresh();
    slideshow.imagesLoaded = false; // District loads every photo eagerly once the carousel moves
    main.go(0);

    // With adapted ratios, the carousel is as tall as the photo showing; measure the new one.
    const first = slideshow.slideshow.querySelector(".splide__slide img.product__media-image");
    first?.setAttribute("loading", "eager");
    const fitHeight = () => {
      const active = slideshow.slideshow.querySelector(".splide__slide.is-active .product__media-image");
      if (slideshow.mediaRatio === "adapt" && active) slideshow.slideshowList.style.height = `${active.offsetHeight}px`;
    };
    fitHeight();
    first?.addEventListener("load", fitHeight, { once: true });
    if (slideshow.thumbnailsContainer) setTimeout(() => slideshow.recalculateThumbnailContainerWidth(), 50);
  }

  // Too late once District has started: its copies are already made.
  if (!customElements.get("media-slideshow")) {
    document.querySelectorAll(`${HIDDEN_SLIDES} img`).forEach(park);
  }

  customElements.whenDefined("media-slideshow").then(() => {
    const MediaSlideshow = customElements.get("media-slideshow");

    // Slides District kept on the page (normally none of the hidden ones) need their photos.
    document.querySelectorAll("media-slideshow").forEach(unpark);

    // District sets every photo to load eagerly on the first slide change. A gallery can have
    // dozens, and the browser already loads the ones next to the one showing.
    const originalLoadImages = MediaSlideshow.prototype.loadImages;
    if (originalLoadImages && !originalLoadImages.florettaWrapped) {
      const loadImages = function (...args) {
        if (this.dataset.florettaGallery === "true") return undefined;
        return originalLoadImages.apply(this, args);
      };
      loadImages.florettaWrapped = true;
      MediaSlideshow.prototype.loadImages = loadImages;
    }

    const originalAddSlidesBack = MediaSlideshow.prototype.addSlidesBack;
    if (originalAddSlidesBack && !originalAddSlidesBack.florettaWrapped) {
      const addSlidesBack = function (...args) {
        const result = originalAddSlidesBack.apply(this, args);
        unpark(this);
        return result;
      };
      addSlidesBack.florettaWrapped = true;
      MediaSlideshow.prototype.addSlidesBack = addSlidesBack;
    }

    // District records hidden slides under their index in the current slide list, but reads
    // them back as indexes into the original list. These differ once slides have been hidden
    // before, so the second variant change brought back the wrong photos (e.g. 5 of 27).
    // Rebuild the record from what is actually on the page.
    const originalRemoveSlides = MediaSlideshow.prototype.removeSlides;
    if (originalRemoveSlides && !originalRemoveSlides.florettaWrapped) {
      const removeSlides = function (...args) {
        const result = originalRemoveSlides.apply(this, args);
        if (this.originalMainSlides?.length && this.removedSlides) {
          const onPage = new Set(
            [...this.slideshow.querySelectorAll(".splide__slide")].map((slide) => slide.dataset.mediaId)
          );
          this.removedSlides.clear();
          this.originalMainSlides.forEach((main, index) => {
            if (onPage.has(main.mediaId)) return;
            this.removedSlides.set(index, {
              main,
              thumbnail: this.originalThumbnailSlides?.[index] ?? null,
              thumbnailElement: this.originalThumbnailElements?.[index] ?? null
            });
          });
        }
        return result;
      };
      removeSlides.florettaWrapped = true;
      MediaSlideshow.prototype.removeSlides = removeSlides;
    }

    // District's lightbox (click a photo) keeps the list of photos it saw on start, including
    // other variants' photos, which are parked and showed up blank, and only those first slides
    // open it, so it stopped opening after a variant change. Instead, on each click, open it
    // with the photos the gallery is showing now. Runs in the capture phase so District's own
    // click handler (with its stale index) never sees the click.
    function lightboxSlides(slideshow) {
      return slideshow.main.Components.Slides.get(true)
        .filter(({ slide }) => slide.dataset.mediaType === "image" && slide.offsetParent !== null);
    }

    document.addEventListener(
      "click",
      (event) => {
        const image = event.target.closest?.(".media__main img.product__media-image");
        const slideshow = image?.closest("media-slideshow");
        if (!slideshow?.modal || !slideshow.main || slideshow.dataset.lightbox !== "true") return;
        const slides = lightboxSlides(slideshow);
        const index = slides.findIndex(({ slide }) => slide.contains(image));
        if (index < 0) return;
        event.stopPropagation();
        slideshow.florettaLightboxSlides = slides;
        slideshow.lightboxImages = slides.map(({ slide }) => slide.querySelector("img.product__media-image"));
        slideshow.showLightbox(index);
      },
      true
    );

    // Arrows in the lightbox also move the gallery, by lightbox index; translate that to the
    // gallery's own slide index, which differs when the gallery has videos.
    for (const name of ["showPreviousLightboxImage", "showNextLightboxImage"]) {
      const originalMove = MediaSlideshow.prototype[name];
      if (!originalMove || originalMove.florettaWrapped) continue;
      const move = function (...args) {
        const slides = this.florettaLightboxSlides;
        if (!slides || !this.main) return originalMove.apply(this, args);
        const go = this.main.go;
        this.main.go = (index) => go.call(this.main, slides[index]?.index ?? index);
        try {
          return originalMove.apply(this, args);
        } finally {
          delete this.main.go;
        }
      };
      move.florettaWrapped = true;
      MediaSlideshow.prototype[name] = move;
    }

    const original = MediaSlideshow.prototype.updateVariantImages;
    if (!original || original.florettaWrapped) return;

    function mediaInfo(slideshow) {
      if (!slideshow.florettaMedia) {
        const data = slideshow.parentElement.querySelector("script[data-floretta-media-alts]");
        slideshow.florettaMedia = data ? JSON.parse(data.textContent) : {};
      }
      return slideshow.florettaMedia;
    }

    function updateVariantImages(variant) {
      // A gallery carousel has just the variant's photos: nothing to show or hide.
      if (this.dataset.florettaGallery === "true") {
        this.florettaStartAtFirst = true;
        return;
      }
      if (!variant || this.dataset.hideVariants !== "true" || !this.originalMainSlides) {
        return original.call(this, variant);
      }

      const media = mediaInfo(this);
      const prefix = `${this.id.replace("MediaSlideshow-", "")}-`;
      const infoFor = (element) => media[(element.dataset.mediaId || "").replace(prefix, "")] || {};
      const altKey = variant.option2 ? `${variant.option2}_${variant.option1}` : null;
      const useAltKeys = Boolean(altKey) && this.originalMainSlides.some(({ element }) => infoFor(element).alt === altKey);

      this.originalMainSlides.forEach(({ element }) => {
        const { alt = "", attached = false } = infoFor(element);
        if (useAltKeys) {
          element.dataset.variantImage = String(alt !== altKey);
        } else {
          element.dataset.variantImage = String(alt.includes("_") ? false : attached);
        }
      });

      // With alt keys, no photo should be kept just for being the variant's attached image.
      const result = original.call(this, useAltKeys ? { ...variant, featured_media: null } : variant);
      // The variant's photos are a new set, so start from the first one. District would stay on the
      // photo that was showing, or (setActiveMedia, below) jump to the variant's attached photo,
      // which can be anywhere in the set; the thumbnail row then opened scrolled partway along.
      this.florettaStartAtFirst = useAltKeys;
      if (useAltKeys) this.main.go(0);
      return result;
    }

    updateVariantImages.florettaWrapped = true;
    MediaSlideshow.prototype.updateVariantImages = updateVariantImages;

    const originalSetActiveMedia = MediaSlideshow.prototype.setActiveMedia;
    if (originalSetActiveMedia && !originalSetActiveMedia.florettaWrapped) {
      const setActiveMedia = function (...args) {
        if (this.florettaStartAtFirst) return;
        return originalSetActiveMedia.apply(this, args);
      };
      setActiveMedia.florettaWrapped = true;
      MediaSlideshow.prototype.setActiveMedia = setActiveMedia;
    }
  });

  // VariantSelects fetches the product section for the new variant, then calls updateMedia.
  // Keep that response for updateMedia, which swaps in the new carousel when a gallery is
  // involved: the new variant has one, or the old one did and the new one has the regular photos.
  // (Also before updateMedia returns early for a variant without a featured photo.)
  customElements.whenDefined("variant-selects").then(() => {
    const VariantSelects = customElements.get("variant-selects");
    const handleUpdateProductInfo = VariantSelects.prototype.handleUpdateProductInfo;
    const updateMedia = VariantSelects.prototype.updateMedia;
    if (!handleUpdateProductInfo || !updateMedia || updateMedia.florettaWrapped) return;

    VariantSelects.prototype.handleUpdateProductInfo = function (...args) {
      const callback = handleUpdateProductInfo.apply(this, args);
      return (html) => {
        this.florettaResponse = html;
        try {
          return callback(html);
        } finally {
          this.florettaResponse = null;
        }
      };
    };

    VariantSelects.prototype.updateMedia = function (...args) {
      const id = `MediaSlideshow-${this.dataset.section}`;
      const slideshow = document.getElementById(id);
      const next = this.florettaResponse?.getElementById(id);
      if (slideshow?.main && next) {
        const gallery = next.dataset.florettaGallery === "true";
        const showing = slideshow.dataset.florettaGallery === "true";
        const same = gallery && showing && slideIds(next) === slideIds(slideshow);
        if ((gallery || showing) && !same) adoptSlides(slideshow, next);
      }
      return updateMedia.apply(this, args);
    };
    VariantSelects.prototype.updateMedia.florettaWrapped = true;
  });
})();
